import React from 'react';
import { View } from 'react-native';

import { Button, Text } from '../../components';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { SecondaryButton } from '../../design/components';
import LeadContactFields, { hasLeadContactValues } from './LeadContactFields';
import { createLeadDetailStyles } from './LeadDetailView.styles';
import LeadTextAction from './LeadTextAction';

// One lead contact on the lead detail: read-only card with call/email links, or its edit form.
const LeadContactBlock = ({
	contact,
	label,
	field,
	isLast = false,
	editingContactField,
	canManageClientsLeads,
	contactDraft,
	onChangeContactDraft,
	isSaving,
	onCancelEdit,
	onSaveContact,
	onOpenContactForm,
	onOpenContactLink,
}) => {
	const styles = useThemedStyles(createLeadDetailStyles);

	if (editingContactField === field) {
		return (
			<View style={[styles.contactBlock, isLast && styles.contactBlockLast]}>
				<Text style={[styles.contactLabel, styles.contactFormLabel]}>{label}</Text>
				<LeadContactFields
					values={contactDraft}
					onChange={onChangeContactDraft}
					disabled={isSaving}
				/>
				{
					Boolean(contact) && (
						<Text style={styles.fieldHint}>Changes update this contact everywhere it is used in Sentry.</Text>
					)
				}
				<View style={styles.contactFormActions}>
					<SecondaryButton
						label="Cancel"
						onPress={onCancelEdit}
						disabled={isSaving}
						style={styles.contactFormButton}
					/>
					<Button
						label={contact ? 'Save contact' : 'Add contact'}
						onPress={onSaveContact}
						secondary={true}
						loading={isSaving}
						disabled={isSaving || !hasLeadContactValues(contactDraft)}
						style={styles.contactFormButton}
					/>
				</View>
			</View>
		);
	}

	const canEditContact = canManageClientsLeads && !editingContactField;

	if (!contact) {
		return (
			<View style={[styles.contactBlock, isLast && styles.contactBlockLast]}>
				<Text style={styles.contactLabel}>{label}</Text>
				<Text style={styles.emptyText}>No contact assigned</Text>
				{
					canEditContact && (
						<LeadTextAction label="Add contact" onPress={() => onOpenContactForm(field)} />
					)
				}
			</View>
		);
	}

	const normalizedPhone = contact.phoneNumber?.replace(/[^\d+]/g, '') || '';

	return (
		<View style={[styles.contactBlock, isLast && styles.contactBlockLast]}>
			<Text style={styles.contactLabel}>{label}</Text>
			<Text style={styles.contactName}>{contact.name || 'Unnamed contact'}</Text>
			{Boolean(contact.role) && <Text style={styles.contactRole}>{contact.role}</Text>}
			{
				Boolean(contact.phoneNumber) && (
					<LeadTextAction
						icon="phone"
						label={contact.phoneNumber}
						onPress={() => onOpenContactLink(`tel:${normalizedPhone}`, 'Unable to start phone call')}
					/>
				)
			}
			{
				Boolean(contact.email) && (
					<LeadTextAction
						icon="mail"
						label={contact.email}
						onPress={() => onOpenContactLink(`mailto:${contact.email}`, 'Unable to open mail app')}
					/>
				)
			}
			{
				canEditContact && (
					<LeadTextAction label="Edit contact" onPress={() => onOpenContactForm(field)} />
				)
			}
		</View>
	);
};

export default LeadContactBlock;
